/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  type EntityFilterQuery,
  CATALOG_FILTER_EXISTS,
} from '@backstage/catalog-client';
import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  entityPresentationApiRef,
  EntityDisplayName,
  EntityRefPresentationSnapshot,
} from '@backstage/plugin-catalog-react';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete, {
  AutocompleteChangeReason,
} from '@material-ui/lab/Autocomplete';
import React, { useCallback, useEffect, useState } from 'react';
import useAsync from 'react-use/esm/useAsync';
import {
  EntityPickerRenderedStringProps,
  EntityPickerRenderedStringUiOptions,
  EntityPickerRenderedStringFilterQuery,
} from './schema';
import { renderString } from 'nunjucks';
import { VirtualizedListbox } from '../MultiEntityPickerRenderedString/VirtualizedListbox';

export { EntityPickerRenderedStringSchema } from './schema';

/**
 * The underlying component that is rendered in the form for the `EntityPickerRenderedString`
 * field extension. Single-entity selection version of MultiEntityPickerRenderedString.
 */
export const EntityPickerRenderedString = (props: EntityPickerRenderedStringProps) => {
  const {
    onChange,
    schema: { title = 'Entity', description = 'An entity from the catalog' },
    required,
    uiSchema,
    rawErrors,
    formData,
    idSchema,
    formContext,
  } = props;

  const [catalogFilter, setCatalogFilter] = useState<EntityFilterQuery>();

  useEffect(() => {
    // Handle entity references that come in $kind:namespace/name format
    const processedFormData = Object.entries(formContext.formData).reduce((acc, [key, value]) => {
      if (typeof value === 'string' && value.startsWith('$')) {
        // Parse $kind:namespace/name format
        const match = value.match(/^\$([^:]+):(.+)$/);
        if (match) {
          const [, kind, namespacedName] = match;
          const [namespace, name] = namespacedName.split('/');
          acc[key] = {
            kind: kind.charAt(0).toUpperCase() + kind.slice(1), // Capitalize kind
            namespace,
            name,
          };
        } else {
          acc[key] = value;
        }
      } else {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const filter = buildCatalogFilter(uiSchema, {
      parameters: processedFormData,
    });

    setCatalogFilter(filter);
  }, [formContext.formData, uiSchema]);

  const defaultKind = uiSchema['ui:options']?.defaultKind;
  const defaultNamespace = uiSchema['ui:options']?.defaultNamespace || undefined;

  const catalogApi = useApi(catalogApiRef);
  const entityPresentationApi = useApi(entityPresentationApiRef);

  const { value: entities, loading } = useAsync(async () => {
    // Ensure this only runs when catalogFilter is set
    if (!catalogFilter) {
      return { entities: [], entityRefToPresentation: new Map() };
    }

    const { items } = await catalogApi.getEntities(
      catalogFilter ? { filter: catalogFilter } : undefined,
    );

    const entityRefToPresentation = new Map<
      string,
      EntityRefPresentationSnapshot
    >(
      await Promise.all(
        items.map(async item => {
          const presentation = await entityPresentationApi.forEntity(item)
            .promise;
          return [stringifyEntityRef(item), presentation] as [
            string,
            EntityRefPresentationSnapshot,
          ];
        }),
      ),
    );
    return { entities: items, entityRefToPresentation };
  }, [catalogFilter]);

  // Reset the field value when catalog filter changes and current value is no longer valid
  useEffect(() => {
    if (formData && entities?.entities) {
      // If there are no entities available, reset the value
      if (entities.entities.length === 0) {
        onChange(undefined);
        return;
      }

      // If there are entities but current value is not valid, reset it
      const isCurrentValueValid = entities.entities.some(entity =>
        stringifyEntityRef(entity) === formData
      );

      if (!isCurrentValueValid) {
        onChange(undefined);
      }
    }
  }, [entities, formData, onChange]);

  const allowArbitraryValues = uiSchema['ui:options']?.allowArbitraryValues ?? true;

  const onSelect = useCallback(
    (_: any, ref: string | Entity | null, reason: AutocompleteChangeReason) => {
      if (!ref) {
        onChange(undefined);
        return;
      }

      if (typeof ref !== 'string') {
        // if ref does not exist: pass 'undefined' to trigger validation for required value
        const value = ref ? stringifyEntityRef(ref as Entity) : undefined;
        onChange(value);
        return;
      }

      if (reason === 'blur' || reason === 'create-option') {
        // Add in default namespace, etc.
        let entityRef = ref;
        try {
          // Attempt to parse the entity ref into it's full form.
          entityRef = stringifyEntityRef(
            parseEntityRef(ref as string, {
              defaultKind,
              defaultNamespace,
            }),
          );
        } catch (err) {
          // If the passed in value isn't an entity ref, do nothing.
        }

        // We need to check against formData here as that's the previous value for this field.
        if (formData === ref || allowArbitraryValues) {
          onChange(entityRef);
          return;
        }
      }

      onChange(undefined);
    },
    [onChange, formData, defaultKind, defaultNamespace, allowArbitraryValues],
  );

  useEffect(() => {
    if (required && !allowArbitraryValues && entities?.entities?.length === 1 && !formData) {
      onChange(stringifyEntityRef(entities?.entities[0]));
    }
  }, [entities, onChange, required, allowArbitraryValues, formData]);

  // Calculate the current value for the Autocomplete
  const currentValue = React.useMemo(() => {
    if (formData) {
      // If we already have a value, try to find the matching entity
      const matchingEntity = entities?.entities?.find(entity =>
        stringifyEntityRef(entity) === formData
      );
      return matchingEntity || formData;
    }
    if (required && !allowArbitraryValues && entities?.entities?.length === 1) {
      // If there's only one option and it's required, auto-select it
      return entities?.entities[0];
    }
    return null;
  }, [formData, required, allowArbitraryValues, entities]);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
    >
      <Autocomplete
        disabled={
          required && !allowArbitraryValues && entities?.entities?.length === 1
        }
        id={idSchema?.$id}
        value={currentValue}
        loading={loading}
        onChange={onSelect}
        options={entities?.entities || []}
        renderOption={option => <EntityDisplayName entityRef={option} />}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          if (option && typeof option === 'object' && 'metadata' in option) {
            return option.metadata?.title || option.metadata?.name || '';
          }
          return '';
        }}
        autoSelect
        freeSolo={allowArbitraryValues}
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            margin="dense"
            helperText={description}
            FormHelperTextProps={{
              margin: 'dense',
              style: { marginLeft: 0 },
            }}
            variant="outlined"
            required={required}
            InputProps={{
              ...params.InputProps,
              required: formData?.length === 0 && required,
            }}
          />
        )}
        ListboxComponent={VirtualizedListbox}
      />
    </FormControl>
  );
};

/**
 * Converts a special `{exists: true}` value to the `CATALOG_FILTER_EXISTS` symbol.
 */
function convertOpsValues(
  value: Exclude<any, Array<any>>,
): string | symbol {
  if (typeof value === 'object' && value.exists) {
    return CATALOG_FILTER_EXISTS;
  }
  return value?.toString();
}

/**
 * Clean entity reference prefixes from rendered values
 */
function cleanEntityRefPrefix(value: string): string {
  // Remove prefixes like "system:", "component:", "resource:", etc.
  const match = value.match(/^[a-z]+:(.+)$/);
  return match ? match[1] : value;
}

/**
 * Converts schema filters to entity filter query, replacing `{exists:true}` values
 * with the constant `CATALOG_FILTER_EXISTS`.
 */
function convertSchemaFiltersToQuery(
  schemaFilters: EntityPickerRenderedStringFilterQuery,
  context: object,
): Exclude<EntityFilterQuery, Array<any>> {
  const query: EntityFilterQuery = {};
  for (const [key, value] of Object.entries(schemaFilters)) {
    if (typeof value === 'string') {
      const renderedValue = renderString(value, context);
      query[key] = cleanEntityRefPrefix(renderedValue);
    } else if (Array.isArray(value)) {
      query[key] = value.map(v => {
        const renderedValue = renderString(v, context);
        return cleanEntityRefPrefix(renderedValue);
      });
    } else {
      query[key] = convertOpsValues(value);
    }
  }
  return query;
}

/**
 * Builds an `EntityFilterQuery` based on the `uiSchema` passed in.
 */
function buildCatalogFilter(
  uiSchema: EntityPickerRenderedStringProps['uiSchema'],
  context: object,
): EntityFilterQuery | undefined {
  const catalogFilter: EntityPickerRenderedStringUiOptions['catalogFilter'] | undefined =
    uiSchema['ui:options']?.catalogFilter;

  if (!catalogFilter) {
    return { 'metadata.title': CATALOG_FILTER_EXISTS };
  }

  if (Array.isArray(catalogFilter)) {
    return catalogFilter.map(schemaFilter =>
      convertSchemaFiltersToQuery(schemaFilter, context),
    );
  }

  const query = convertSchemaFiltersToQuery(catalogFilter, context);

  // Adiciona o filtro de title ao filtro existente
  query['metadata.title'] = CATALOG_FILTER_EXISTS;

  return query;
}
